import { useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  MARKETING_SME_PLANS,
  MARKETING_PLAN_LABEL,
  type MarketingPlanId,
} from '@/lib/marketing/planCatalog';
import { cn } from '@/lib/utils';

function formatNum(n: number): string {
  return n >= 1000 ? n.toLocaleString('it-IT') : String(n);
}

function formatPrice(planId: MarketingPlanId, priceMonthly: number, t: (k: string) => string): string {
  if (planId === 'freemium_trial') return t('marketing.pricing.trial.price');
  return `€${priceMonthly}${t('marketing.pricing.perMonth')}`;
}

export function MarketingPricingCompare() {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  const limitRows: Array<{ key: 'positions' | 'candidates' | 'aiCompany' | 'seats'; label: string }> = [
    { key: 'positions', label: t('marketing.compare.row.positions') },
    { key: 'candidates', label: t('subscription.compare.row.candidates') },
    { key: 'aiCompany', label: t('subscription.compare.row.aiCompany') },
    { key: 'seats', label: t('subscription.compare.row.seats') },
  ];

  const featureRows: Array<{ label: string; trial: boolean; paid: boolean }> = [
    { label: t('marketing.compare.row.jdAi'), trial: true, paid: true },
    { label: t('marketing.compare.row.brandedPage'), trial: true, paid: true },
    { label: t('marketing.compare.row.screening'), trial: true, paid: true },
    { label: t('marketing.compare.row.interviewQuestions'), trial: true, paid: true },
    { label: t('subscription.compare.row.callTranscription'), trial: false, paid: true },
    { label: t('marketing.compare.row.interviewReport'), trial: true, paid: true },
  ];

  return (
    <div className="space-y-6">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger
          className={cn(
            'flex w-full items-center justify-between gap-3 rounded-xl border border-border/60',
            'bg-card px-4 py-3.5 text-left text-sm font-semibold shadow-sm',
            'hover:bg-muted/40 transition-colors',
          )}
        >
          <span>{t('marketing.pricing.compareTitle')}</span>
          <ChevronDown
            className={cn('h-5 w-5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')}
            aria-hidden
          />
        </CollapsibleTrigger>

        <CollapsibleContent className="overflow-hidden transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <div className="pt-4 space-y-4">
          <div className="overflow-x-auto rounded-xl border border-border/60 shadow-sm">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-muted/50 text-xs">
                  <th className="text-left p-3 font-medium w-[28%]">{t('subscription.compare.feature')}</th>
                  {MARKETING_SME_PLANS.map((p) => (
                    <th key={p.planId} className="text-center p-3 font-medium min-w-[100px]">
                      <div className="font-display">{MARKETING_PLAN_LABEL[p.planId]}</div>
                      <div className="text-muted-foreground font-normal mt-0.5">
                        {formatPrice(p.planId, p.priceMonthly, t)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {limitRows.map((row) => (
                  <tr key={row.key} className="border-t border-border/60">
                    <td className="p-3 text-muted-foreground">{row.label}</td>
                    {MARKETING_SME_PLANS.map((p) => {
                      const v = p.limits[row.key];
                      return (
                        <td key={p.planId} className="p-3 text-center text-xs font-medium">
                          {v === 0 ? (
                            <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                          ) : (
                            formatNum(v)
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {featureRows.map((row) => (
                  <tr key={row.label} className="border-t border-border/60">
                    <td className="p-3 text-muted-foreground">{row.label}</td>
                    {MARKETING_SME_PLANS.map((p) => {
                      const ok = p.planId === 'freemium_trial' ? row.trial : row.paid;
                      return (
                        <td key={p.planId} className="p-3 text-center">
                          {ok ? (
                            <Check className="h-4 w-4 text-brand-accent mx-auto" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-border/60 bg-muted/30 p-4 space-y-2 text-xs text-muted-foreground">
            <p>
              <strong className="text-foreground">{t('subscription.compare.creditsExplainer.title')}</strong>
            </p>
            <p>{t('subscription.compare.creditsExplainer.candidatesAi')}</p>
            <p>{t('subscription.compare.creditsExplainer.aiCredits')}</p>
            <p>{t('marketing.compare.humanNote')}</p>
          </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
